import {FC, useMemo} from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
} from '@mui/material';
import {Controller, useForm, useFormContext} from 'react-hook-form';
import {CompanyDto, ContractDto} from '../../../../global/api';
import {useGetCompaniesQuery} from '../../../../global/services/companies';
import {Stack} from '@mui/system';
import {get, isEqual, some} from 'lodash';
import {useDebouncedCompanyKeyWord} from '../../hooks/use-debounced-company-key-word';
import {generalInfoModalClose} from '../../stores/contract-general-info-modal.store';

export const ContractGeneralInfoModal: FC = () => {
  const {keyWord, inputValue, handleInputChange} = useDebouncedCompanyKeyWord();
  const {getValues, setValue} = useFormContext<ContractDto & {company: CompanyDto}>();
  const company = getValues('company');

  const {
    reset,
    control,
    handleSubmit,
    formState: {isDirty},
  } = useForm<{company: CompanyDto}>({
    defaultValues: {company},
  });

  const {data, loading} = useGetCompaniesQuery({keyWord});
  const title = useMemo(() => getValues('company.fullCompanyName'), []);

  const options = useMemo(() => {
    const list = get(data, 'content', []) || [];
    return company && !some(list, {id: company.id}) ? [company, ...list] : list;
  }, [company, data]);

  const handleCloseModal = () => {
    reset();
    generalInfoModalClose();
  };

  const handleSaveModal = (values: {company: CompanyDto}) => {
    setValue('company', values.company, {shouldDirty: true});
    handleCloseModal();
  };

  return (
    <Dialog fullWidth maxWidth="md" open onClose={handleCloseModal}>
      <StyledDialogTitle>{title}</StyledDialogTitle>
      <form onSubmit={handleSubmit(handleSaveModal)}>
        <StyledDialogContent>
          <Controller
            name="company"
            rules={{required: 'Обязательно для заполнения'}}
            control={control}
            render={({field: {value, onChange}, fieldState: {error}}) => (
              <Autocomplete
                value={value}
                onChange={(_, value) => onChange(value)}
                fullWidth
                getOptionLabel={(option) => `${option.fullCompanyName}, ИНН: ${option.inn}`}
                isOptionEqualToValue={(option, value) => isEqual(option, value)}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                options={options}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Выбор компании"
                    placeholder="Название / ИНН / комментарий"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="company.inn"
              control={control}
              render={({field}) => <TextField value={field.value ?? ''} disabled label="ИНН" size="small" />}
            />
            <Controller
              name="company.ogrn"
              control={control}
              render={({field}) => <TextField value={field.value ?? ''} disabled label="ОГРН" size="small" />}
            />
            <Controller
              name="company.website"
              control={control}
              render={({field}) => <TextField value={field.value ?? ''} disabled label="Сайт компании" size="small" />}
            />
          </Stack>
          <Controller
            name="company.description"
            control={control}
            render={({field}) => (
              <TextField value={field.value ?? ''} disabled label="Комментарий" size="small" multiline rows={2} />
            )}
          />
        </StyledDialogContent>
        <DialogActions>
          <Button type="submit" disabled={!isDirty}>
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const StyledDialogTitle = styled(DialogTitle)({
  padding: '16px 24px 0',
});

const StyledDialogContent = styled(DialogContent)({
  '&.MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '20px 24px',
  },
});
